"use client";
import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaTelegramPlane,
  FaArrowLeft,
  FaInstagram,
  FaFacebookF,
} from "react-icons/fa";
import { sendMessage } from "../../actions/sendMessage";
import { submitContactFormEvent } from "../../utils/gtm-events/contact";

function ThankYou({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="flex flex-col items-center justify-center text-center py-20 px-6 bg-gradient-to-b from-[var(--primary)]/80 to-black/70 rounded-2xl shadow-xl backdrop-blur-md border border-white/10 max-w-lg mx-auto text-white"
    >
      <div className="bg-[var(--secondary)] h-24 w-24 rounded-full flex items-center justify-center mb-6 shadow-[0_0_40px_var(--secondary)] animate-bounce">
        <FaTelegramPlane className="text-white size-10" />
      </div>

      <h2 className="text-4xl font-extrabold text-black drop-shadow-lg">
        Thank You!
      </h2>
      <p className="mt-4 text-black text-lg leading-relaxed max-w-md">
        Your message has been sent successfully.
      </p>
      <p className="text-black text-lg">Our team will contact you soon.</p>

      <button
        onClick={onClose}
        className="flex items-center gap-2 mt-8 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-[var(--secondary)] font-medium hover:bg-white/20 hover:underline transition-all text-black"
      >
        <FaArrowLeft className="size-3" /> Return to Contact Form
      </button>
    </motion.div>
  );
}

export default function Layout6({ data }: { data: any }) {
  if (!data || data?.isLive === false) return null;

  const addr = data?.addresses?.[0] || {};
  const socials = data?.socialLinks || [];

  // ✅ Simplified form data: name + email + mobile + message
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // ✅ Send data with updated field names
      const res = await sendMessage(formData);
      if (res.type === "success") {
        submitContactFormEvent({
          formName: "Contact Us",
          formLocation: window.location.pathname,
        });
        setShowThankYou(true);
      } else alert("Failed to send message.");
    } catch {
      alert("Error submitting form. Try again later.");
    } finally {
      setIsSubmitting(false);
      setFormData({
        name: "",
        email: "",
        mobile: "",
        message: "",
      });
    }
  };

  if (showThankYou)
    return (
      <div className="flex justify-center min-h-[500px]">
        <ThankYou open={showThankYou} onClose={() => setShowThankYou(false)} />
      </div>
    );

  const inputClass =
    "w-full bg-transparent border-b border-white/40 focus:border-[var(--secondary)] py-3 text-white placeholder-white outline-none transition";

  const textareaClass =
    "w-full bg-transparent border border-white/40 focus:border-[var(--secondary)] rounded-2xl py-4 px-5 text-white placeholder-white outline-none resize-none transition";


  return (
    <section
      className="relative py-28 px-6 lg:px-20 overflow-hidden text-white"
      style={{
        backgroundImage: `linear-gradient(90deg, rgba(0,0,0,0.45), rgba(0,0,0,0.5)), url(${data?.image?.data?.attributes?.url})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-white/5 mix-blend-overlay" />
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-[var(--secondary)] opacity-25 blur-[200px] rounded-full" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-[var(--secondary)] opacity-25 blur-[200px] rounded-full" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h1 className="text-5xl font-bold text-white">
            Contact <span className="text-[var(--secondary)]">Us</span>
          </h1>
          {data?.description && (
            <p className="text-gray-200 mt-4 text-lg max-w-3xl mx-auto">
              {data.description}
            </p>
          )}
        </motion.div>
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-14 items-stretch">
          {/* ✅ CONTACT FORM */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="relative p-10 bg-white/15 backdrop-blur-xl border border-white/20 rounded-3xl shadow-[0_0_40px_rgba(255,255,255,0.1)]"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--secondary)_0%,transparent_70%)] opacity-15 blur-2xl"></div>

            <form
              onSubmit={handleSubmit}
              className="relative z-10 flex flex-col gap-6"
            >
              {/* ✅ Single full name field */}
              <input
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Full Name"
                className={inputClass}
              />

              <input
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                className={inputClass}
              />

              <input
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="Mobile Number"
                className={inputClass}
              />

              <textarea
                name="message"
                required
                rows={5}
                value={formData.message}
                onChange={handleChange}
                placeholder="Your Message"
                className={textareaClass}
              />

              <motion.button
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 0 20px var(--secondary)",
                }}
                whileTap={{ scale: 0.98 }}
                disabled={isSubmitting}
                className="w-full mt-4 bg-[var(--secondary)] text-white py-4 rounded-xl font-semibold shadow-lg transition hover:brightness-110"
              >
                {isSubmitting
                  ? "Sending..."
                  : data?.button?.text || "Contact Now"}
              </motion.button>
            </form>
          </motion.div>

          {/* ✅ Contact Info Section */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col justify-between text-white"
          >
            <div className="space-y-8">
              <div className="flex gap-4 items-start">
                <FaMapMarkerAlt className="text-[var(--secondary)] mt-1" />
                <div>
                  <div className="text-[var(--secondary)] font-medium">
                    Visit Us
                  </div>
                  <p className="text-gray-200 leading-relaxed">
                    {addr.address}
                    <br />
                    {addr.city}, {addr.state}, {addr.country} –{" "}
                    {addr.postalCode}
                  </p>
                </div>
              </div>

              {addr.email && (
                <div className="flex gap-4 items-start">
                  <FaEnvelope className="text-[var(--secondary)] mt-1" />
                  <div>
                    <div className="text-[var(--secondary)] font-medium">
                      Email
                    </div>
                    <a
                      href={`mailto:${addr.email}`}
                      className="hover:underline text-gray-200"
                    >
                      {addr.email}
                    </a>
                  </div>
                </div>
              )}

              {addr.Phone && (
                <div className="flex gap-4 items-start">
                  <FaPhone className="text-[var(--secondary)] mt-1" />
                  <div>
                    <div className="text-[var(--secondary)] font-medium">
                      Call Us
                    </div>
                    <a
                      href={`tel:${addr.Phone}`}
                      className="hover:underline text-gray-200"
                    >
                      +91 {addr.Phone}
                    </a>
                  </div>
                </div>
              )}

              {socials.length > 0 && (
                <div className="flex gap-4 pt-4">
                  {socials.map((item: any) => (
                    <a
                      key={item.id}
                      href={item.url}
                      target={item.newTab ? "_blank" : "_self"}
                      rel="noreferrer"
                      className="p-3 rounded-full border border-white/30 hover:bg-[var(--secondary)] hover:border-[var(--secondary)] transition"
                    >
                      {item.social === "INSTAGRAM" && (
                        <FaInstagram className="size-5" />
                      )}
                      {item.social === "FACEBOOK" && (
                        <FaFacebookF className="size-5" />
                      )}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
